import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, Spinner } from '@tkpod/ui';
import {
  buildVariantPreviewTabs,
  COMPOSITE_PREVIEW_TAB_ID,
  groupColorWaySwatches,
  resolveGalleryStagePreview,
  resolveSwatchHex,
  sanitizeCssHex,
} from '@tkpod/shared';

type GalleryPrintSide = {
  printAreaId: string;
  displayName: string;
};

type GalleryVariant = {
  id: string;
  title: string | null;
  options: unknown;
  imageUrl: string | null;
  previewJobStatus: string | null;
  colorHex?: string | null;
  externalDeletedAt?: string | null;
  sides: Array<{
    printAreaId: string;
    previewUrl: string | null;
    previewStatus: string | null;
  }>;
};

function ColorSwatchDot({ hex }: { hex: string | null }) {
  const fill = sanitizeCssHex(hex) ?? '#d4d4d4';
  return (
    <span
      aria-hidden
      className="inline-block h-[22px] w-[22px] shrink-0 rounded-full border border-black/20"
      style={{ background: fill }}
    />
  );
}

function GalleryStageStatus({
  status,
}: {
  status: string | null | undefined;
}) {
  if (status === 'FAILED') {
    return (
      <p className="text-center text-xs text-danger">Preview failed</p>
    );
  }
  return (
    <div className="flex items-center justify-center gap-2 text-xs text-text-tertiary">
      <Spinner size="sm" />
      Generating preview
    </div>
  );
}

/**
 * Compact in-page gallery: a square contain preview on the left and a
 * wrapping hex-color swatch grid on the right. Full JPEG opens in a new tab.
 */
export function ProductPreviewGallery({
  productTitle,
  printSides,
  variants,
}: {
  productTitle: string;
  printSides: GalleryPrintSide[];
  variants: GalleryVariant[];
}) {
  const activeVariants = useMemo(
    () => variants.filter((v) => !v.externalDeletedAt),
    [variants],
  );
  const tabs = useMemo(
    () => buildVariantPreviewTabs(printSides),
    [printSides],
  );
  const colorGroups = useMemo(
    () =>
      groupColorWaySwatches(activeVariants, (v) => ({
        options: v.options,
        colorHex: v.colorHex,
        previewJobStatus: v.previewJobStatus,
      })),
    [activeVariants],
  );

  const [tabId, setTabId] = useState(
    () => tabs[0]?.id ?? COMPOSITE_PREVIEW_TAB_ID,
  );
  const [colorKey, setColorKey] = useState(
    () => colorGroups[0]?.colorKey ?? '',
  );

  useEffect(() => {
    if (!tabs.some((t) => t.id === tabId)) {
      setTabId(tabs[0]?.id ?? COMPOSITE_PREVIEW_TAB_ID);
    }
  }, [tabs, tabId]);

  useEffect(() => {
    if (!colorGroups.some((g) => g.colorKey === colorKey)) {
      setColorKey(colorGroups[0]?.colorKey ?? '');
    }
  }, [colorGroups, colorKey]);

  if (activeVariants.length === 0) return null;

  const selectedGroup =
    colorGroups.find((g) => g.colorKey === colorKey) ?? colorGroups[0];
  const selectedVariant = selectedGroup?.representative ?? activeVariants[0];
  const stage = resolveGalleryStagePreview(tabId, selectedVariant);
  const activeTab =
    tabs.find((t) => t.id === tabId) ??
    tabs[0] ?? { id: tabId, label: 'Preview' };
  const stageAlt =
    `${productTitle} ${selectedGroup?.colorLabel ?? ''} ${activeTab.label}`.trim();
  const showColorControls = colorGroups.length > 1;
  const showTabs = tabs.length > 1;

  return (
    <Card padding="none">
      <CardHeader title="Preview" />
      <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
        {showTabs ? (
          <div role="tablist" aria-label="Preview sides" className="flex flex-wrap gap-1">
            {tabs.map((tab) => {
              const selected = tab.id === tabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setTabId(tab.id)}
                  className={
                    'rounded px-2.5 py-1 text-xs font-medium transition-colors ' +
                    (selected
                      ? 'border border-border bg-surface-secondary text-text-primary'
                      : 'text-text-tertiary hover:text-text-primary')
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div
          className={
            showColorControls
              ? 'grid grid-cols-1 gap-6 sm:grid-cols-[13rem_minmax(0,1fr)] sm:items-start'
              : undefined
          }
        >
          <div>
            <div className="h-[13rem] w-[13rem] overflow-hidden rounded-lg border border-border bg-surface-secondary">
              {stage.url ? (
                <a
                  href={stage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-full w-full items-center justify-center"
                  title="Open full size"
                >
                  <img
                    src={stage.url}
                    alt={stageAlt}
                    className="h-full w-full object-contain"
                  />
                </a>
              ) : (
                <div className="flex h-full items-center justify-center px-2">
                  <GalleryStageStatus status={stage.status} />
                </div>
              )}
            </div>
            {stage.url ? (
              <a
                href={stage.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 inline-block text-xs font-medium text-text-secondary hover:text-text-primary"
              >
                Open full size
              </a>
            ) : null}
          </div>

          {showColorControls ? (
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                Color
              </p>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
                {colorGroups.map((group) => {
                  const selected = group.colorKey === colorKey;
                  return (
                    <button
                      key={group.colorKey}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setColorKey(group.colorKey)}
                      className={
                        'inline-flex min-w-0 items-center gap-2 rounded border px-2 py-1.5 text-left text-xs font-medium transition-colors ' +
                        (selected
                          ? 'border-border bg-surface-secondary text-text-primary'
                          : 'border-transparent text-text-tertiary hover:border-border hover:text-text-primary')
                      }
                    >
                      <ColorSwatchDot
                        hex={resolveSwatchHex({
                          hex: group.colorHex,
                          colorName: group.colorLabel,
                        })}
                      />
                      <span className="truncate">{group.colorLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
